import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CalendarDays, Clock, Users, Loader2, MapPin, Eye, EyeOff, XCircle, Building2 } from "lucide-react";
import { ALL_SPORTS } from "@/lib/sports";
import { Textarea } from "@/components/ui/textarea";

const statusStyle = { open: "bg-green-100 text-green-800", confirmed: "bg-blue-100 text-blue-800", completed: "bg-slate-100 text-slate-600", cancelled: "bg-red-100 text-red-800" };

export default function CoachSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(searchParams.get("create") === "true");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelOther, setCancelOther] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [venues, setVenues] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(false);
  const CANCEL_REASONS = ["Schedule conflict","Personal emergency","Injury or health issue","Venue unavailable","Insufficient participants","Weather conditions","Client requested cancellation","Other"];
  const [form, setForm] = useState({
    title: "", sport: "basketball", venue_id: "", space_id: "", config_id: "",
    session_date: "", start_time: "18:00",
    duration_minutes: 60, pricing_model: "per_session", price_amount: 500, slot_limit: 15,
    min_players: 4, visibility: "public", description: "", currency: "PHP",
  });
  const navigate = useNavigate();

  const fetchSessions = async () => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      const res = await api.get("/coach/sessions", { params });
      setSessions(res.data);
    } catch { setSessions([]); }
    setLoading(false);
  };

  const fetchVenues = async () => {
    setLoadingVenues(true);
    try {
      const res = await api.get("/coach/available-venues");
      setVenues(res.data);
    } catch { setVenues([]); }
    setLoadingVenues(false);
  };

  useEffect(() => { setLoading(true); fetchSessions(); }, [filter]); // eslint-disable-line
  
  useEffect(() => {
    if (showForm && venues.length === 0) {
      fetchVenues();
    }
  }, [showForm]); // eslint-disable-line

  // Get available spaces for selected venue
  const selectedVenue = venues.find(v => v.venue_id === form.venue_id);
  const availableSpaces = selectedVenue?.spaces || [];
  
  // Get available configs for selected space
  const selectedSpace = availableSpaces.find(s => s.physical_space_id === form.space_id);
  const availableConfigs = selectedSpace?.activity_configurations || [];

  const handleVenueChange = (venueId) => {
    setForm({...form, venue_id: venueId, space_id: "", config_id: ""});
  };

  const handleSpaceChange = (spaceId) => {
    setForm({...form, space_id: spaceId, config_id: ""});
  };

  const handleConfigChange = (configId) => {
    if (!configId || configId === "none") {
      setForm({...form, config_id: ""});
      return;
    }
    const config = availableConfigs.find(c => c.config_id === configId);
    if (config) {
      setForm({
        ...form, 
        config_id: configId, 
        sport: config.activity_type_id,
        slot_limit: config.max_capacity
      });
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.session_date) { toast.error("Title and date are required"); return; }
    setSaving(true);
    try {
      await api.post("/coach/sessions", form);
      toast.success("Session created!");
      setShowForm(false);
      setForm({ title: "", sport: "basketball", venue_id: "", space_id: "", config_id: "",
        session_date: "", start_time: "18:00",
        duration_minutes: 60, pricing_model: "per_session", price_amount: 500, slot_limit: 15,
        min_players: 4, visibility: "public", description: "", currency: "PHP" });
      fetchSessions();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleCancel = async (session) => {
    setCancelTarget(session);
    setCancelReason("");
    setCancelOther("");
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    if (!cancelReason) { toast.error("Please select a reason for cancellation"); return; }
    const reason = cancelReason === "Other" ? (cancelOther.trim() || "Other") : cancelReason;
    setCancelling(true);
    try {
      await api.post(`/coach/sessions/${cancelTarget.session_id}/cancel`, { reason });
      toast.success("Session cancelled");
      setCancelTarget(null);
      fetchSessions();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setCancelling(false);
  };

  const handleComplete = async (sessionId) => {
    try {
      const res = await api.post(`/coach/sessions/${sessionId}/complete`);
      toast.success(`Session completed! Earnings: ₱${res.data.earnings}`);
      fetchSessions();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="coach-sessions-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3">
          <CalendarDays className="w-8 h-8 text-court-orange" />My Sessions
        </h1>
        <div className="flex gap-3">
          <Select value={filter} onValueChange={v => { setFilter(v); setLoading(true); }}>
            <SelectTrigger data-testid="session-filter" className="w-40 h-11 rounded-none border-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="new-session-btn" onClick={() => setShowForm(true)}
            className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase px-6 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
            <Plus className="w-4 h-4 mr-2" />New Session
          </Button>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200">
          <CalendarDays className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No sessions yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create your first training session</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => (
            <div key={s.session_id} className="bg-white border-2 border-slate-100 p-4" data-testid={`session-${s.session_id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1" onClick={() => navigate(`/coach/sessions/${s.session_id}`)} style={{cursor: "pointer"}}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-heading text-base font-bold uppercase tracking-tight">{s.title}</h3>
                    <Badge className={`${statusStyle[s.status]} text-[10px] font-heading uppercase`}>{s.status}</Badge>
                    <Badge variant="outline" className="text-[10px] font-heading uppercase">{s.activity_name || s.sport}</Badge>
                    <Badge variant="outline" className="text-[10px]">{s.visibility === "public" ? <><Eye className="w-3 h-3 mr-0.5 inline" />Public</> : <><EyeOff className="w-3 h-3 mr-0.5 inline" />Invite</>}</Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{s.session_date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{s.start_time} ({s.duration_minutes}min)</span>
                    {s.venue_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.venue_name}{s.space_name && ` - ${s.space_name}`}</span>}
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{s.participants_count}/{s.slot_limit}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <p className="font-heading text-lg font-bold text-court-orange">₱{s.price_amount}</p>
                  {s.status === "open" && (
                    <>
                      <Button size="sm" onClick={() => handleComplete(s.session_id)} className="bg-lime-volt hover:bg-lime-volt/90 text-black rounded-full font-heading uppercase text-[10px] px-3 h-7">Complete</Button>
                      <Button size="sm" variant="outline" onClick={() => handleCancel(s)} className="rounded-full border-2 border-red-200 text-red-500 text-[10px] h-7 px-3" data-testid={`cancel-session-${s.session_id}`}>
                        <XCircle className="w-3 h-3 mr-0.5" />Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Session Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-session-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase">Create Training Session</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs font-heading uppercase">Title</Label>
              <Input data-testid="session-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Basketball Skills Training" className="h-11 rounded-none border-2 mt-1" /></div>
            <div><Label className="text-xs font-heading uppercase">Description</Label>
              <Input data-testid="session-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What will players learn?" className="h-11 rounded-none border-2 mt-1" /></div>
            
            {/* Venue Selection */}
            <div className="p-3 bg-slate-50 border-2 border-slate-200 space-y-3">
              <div className="flex items-center gap-2 text-xs font-heading uppercase text-muted-foreground">
                <Building2 className="w-4 h-4" /> Venue & Space (Optional)
              </div>
              <div>
                <Label className="text-xs font-heading uppercase">Venue</Label>
                <Select value={form.venue_id || "none"} onValueChange={v => handleVenueChange(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="session-venue" className="h-11 rounded-none border-2 mt-1">
                    <SelectValue placeholder={loadingVenues ? "Loading venues..." : "Select venue (optional)"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No venue (online/TBD)</SelectItem>
                    {venues.map(v => (
                      <SelectItem key={v.venue_id} value={v.venue_id}>
                        {v.name} - {v.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {form.venue_id && availableSpaces.length > 0 && (
                <div>
                  <Label className="text-xs font-heading uppercase">Space</Label>
                  <Select value={form.space_id || "none"} onValueChange={v => handleSpaceChange(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="session-space" className="h-11 rounded-none border-2 mt-1">
                      <SelectValue placeholder="Select space" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select a space...</SelectItem>
                      {availableSpaces.map(s => (
                        <SelectItem key={s.space_id} value={s.space_id}>
                          {s.name} ({s.space_type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {form.space_id && availableConfigs.length > 0 && (
                <div>
                  <Label className="text-xs font-heading uppercase">Activity</Label>
                  <Select value={form.config_id || "none"} onValueChange={v => handleConfigChange(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="session-activity" className="h-11 rounded-none border-2 mt-1">
                      <SelectValue placeholder="Select activity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select an activity...</SelectItem>
                      {availableConfigs.map(c => (
                        <SelectItem key={c.config_id} value={c.config_id}>
                          {c.activity_name} (max {c.max_capacity})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {!form.venue_id && (
                <p className="text-[10px] text-muted-foreground">Select a venue to book a specific space, or leave empty for online/TBD sessions.</p>
              )}
            </div>
            
            {/* Sport selection - only show if no config selected */}
            {!form.config_id && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-heading uppercase">Sport</Label>
                  <Select value={form.sport} onValueChange={v => setForm({...form, sport: v})}>
                    <SelectTrigger data-testid="session-sport" className="h-11 rounded-none border-2 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{ALL_SPORTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs font-heading uppercase">Visibility</Label>
                  <Select value={form.visibility} onValueChange={v => setForm({...form, visibility: v})}>
                    <SelectTrigger className="h-11 rounded-none border-2 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="invite_only">Invite Only</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
            )}
            
            {form.config_id && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-heading uppercase">Activity</Label>
                  <div className="h-11 flex items-center px-3 border-2 border-slate-200 bg-slate-50 mt-1 text-sm">
                    {availableConfigs.find(c => c.config_id === form.config_id)?.activity_name || form.sport}
                  </div>
                </div>
                <div><Label className="text-xs font-heading uppercase">Visibility</Label>
                  <Select value={form.visibility} onValueChange={v => setForm({...form, visibility: v})}>
                    <SelectTrigger className="h-11 rounded-none border-2 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="invite_only">Invite Only</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="text-xs font-heading uppercase">Date</Label>
                <Input data-testid="session-date" type="date" value={form.session_date} onChange={e => setForm({...form, session_date: e.target.value})} className="h-11 rounded-none border-2 mt-1" /></div>
              <div><Label className="text-xs font-heading uppercase">Start Time</Label>
                <Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} className="h-11 rounded-none border-2 mt-1" /></div>
              <div><Label className="text-xs font-heading uppercase">Duration</Label>
                <Select value={String(form.duration_minutes)} onValueChange={v => setForm({...form, duration_minutes: parseInt(v)})}>
                  <SelectTrigger className="h-11 rounded-none border-2 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="text-xs font-heading uppercase">Max Players</Label>
                <Input type="number" value={form.slot_limit} onChange={e => setForm({...form, slot_limit: parseInt(e.target.value) || 15})} className="h-11 rounded-none border-2 mt-1" /></div>
              <div><Label className="text-xs font-heading uppercase">Min Players</Label>
                <Input type="number" value={form.min_players} onChange={e => setForm({...form, min_players: parseInt(e.target.value) || 4})} className="h-11 rounded-none border-2 mt-1" /></div>
              <div><Label className="text-xs font-heading uppercase">Price (PHP)</Label>
                <Input data-testid="session-price" type="number" step="0.01" value={form.price_amount} onChange={e => setForm({...form, price_amount: parseFloat(e.target.value) || 0})} className="h-11 rounded-none border-2 mt-1" /></div>
            </div>
            <div><Label className="text-xs font-heading uppercase">Pricing Model</Label>
              <div className="flex gap-3 mt-1">
                {[{v: "per_session", l: "Per Session"}, {v: "per_player", l: "Per Player"}].map(pm => (
                  <button key={pm.v} type="button" onClick={() => setForm({...form, pricing_model: pm.v})}
                    className={`flex-1 py-2.5 border-2 text-xs font-heading uppercase tracking-wide transition-colors ${form.pricing_model === pm.v ? 'border-court-orange bg-court-orange/5 text-court-orange' : 'border-slate-200 text-slate-500'}`}>
                    {pm.l}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{form.pricing_model === "per_player" ? "Each player pays the price" : "Total session fee split among platform"}</p>
            </div>
            <Button data-testid="save-session-btn" onClick={handleCreate} disabled={saving}
              className="w-full bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase h-12 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
              {saving ? "Creating..." : "Create Session"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Session Confirmation Dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={v => { if (!v) setCancelTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="cancel-session-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-red-600">Cancel Session</DialogTitle>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">This will cancel the session. All registered participants will be notified and refunded.</p>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                <p className="font-heading font-bold">{cancelTarget.title}</p>
                <p className="text-xs text-muted-foreground">{cancelTarget.session_date} · {cancelTarget.start_time} ({cancelTarget.duration_minutes}min) · {cancelTarget.sport}</p>
                <p className="text-xs text-muted-foreground">{cancelTarget.participants_count}/{cancelTarget.slot_limit} participants registered</p>
                {cancelTarget.participants_count > 0 && (
                  <p className="text-xs font-medium text-red-700 mt-1">{cancelTarget.participants_count} participant(s) will receive 100% refund</p>
                )}
              </div>

              <div>
                <Label className="text-xs font-medium">Reason for Cancellation *</Label>
                <Select value={cancelReason} onValueChange={setCancelReason}>
                  <SelectTrigger data-testid="session-cancel-reason"><SelectValue placeholder="Select a reason..." /></SelectTrigger>
                  <SelectContent>
                    {CANCEL_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {cancelReason === "Other" && (
                <div>
                  <Label className="text-xs font-medium">Please specify</Label>
                  <Textarea value={cancelOther} onChange={e => setCancelOther(e.target.value)}
                    placeholder="Type your reason here..." className="text-sm" data-testid="session-cancel-other" />
                </div>
              )}

              <p className="text-[11px] text-muted-foreground">
                Cancellations less than 48 hours before the session may affect your reliability score and professional rating.
              </p>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCancelTarget(null)} className="flex-1">Keep Session</Button>
                <Button onClick={confirmCancel} disabled={cancelling || !cancelReason}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-session-cancel">
                  <XCircle className="w-4 h-4 mr-1" />{cancelling ? "Cancelling..." : "Confirm Cancellation"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
